import { CanopyProvider } from '@bloomfieldrobotics/canopy';
import { Route, Routes } from 'react-router-dom';
import APIExplorer from '../apiExplorer/APIExplorer';
import LoginPage from '../auth/LoginPage';
import { BlockDetailsPage } from '../blocks/BlockDetailsPage';
import { CollectionDetailsPage } from '../collections/CollectionDetailsPage';
import { CollectionsPage } from '../collections/CollectionsPage';
import DevSandbox from '../devSandbox/DevSandbox';
import { OnboardingPage } from '../onboarding/OnboardingPage';
import LoadingPlantPage from '../pages/LoadingPlantPage';
import NotFound from '../pages/NotFound';
import PageLayoutAndProviders from '../pages/PageLayoutAndProviders';
import RanchesLayout from '../ranches/RanchesLayout';
import { VarietyReportPage } from '../reports/blueberry/VarietyReportPage';
import { ClusterGradingReportPage } from '../reports/grape/ClusterGradingReportPage';
import { ReportsLandingPage } from '../reports/ReportsLandingPage';
import { ScansPage } from '../scans/ScansPage';
import { SettingsPage } from '../settings/SettingsPage';
import { AuthenticatedRoute } from './components/AuthenticatedRoute';
import { FeatureFlaggedRoute } from './components/FeatureFlaggedRoute';
import { InternalRoute } from './components/InternalRoute';

type Props = {
  loading: boolean;
};

export const AppRouter = ({ loading }: Props) => {
  return (
    <Routes>
      <Route path='/login' element={<LoginPage />} />
      <Route element={<AuthenticatedRoute />}>
        {loading ? (
          <Route index path='*' element={<LoadingPlantPage />} />
        ) : (
          <Route element={<PageLayoutAndProviders />}>
            {/**
             * Customer-facing routes
             */}
            <Route path='/' element={<RanchesLayout />} />
            <Route
              path='/scans'
              element={
                <CanopyProvider>
                  <ScansPage />
                </CanopyProvider>
              }
            />
            <Route path='/settings' element={<SettingsPage />} />
            <Route element={<FeatureFlaggedRoute flag='Reports' />}>
              <Route path='/reports'>
                <Route index element={<ReportsLandingPage />} />

                <Route path='blueberry'>
                  <Route path=':varietyId' element={<VarietyReportPage />} />
                </Route>
                <Route
                  path='grape'
                  element={<FeatureFlaggedRoute flag='GrapeClusterReport' />}
                >
                  <Route
                    path='clusters'
                    element={<ClusterGradingReportPage />}
                  />
                </Route>
              </Route>
              <Route
                path='blocks/:blockId/scan/:scanId'
                element={<BlockDetailsPage />}
              />
            </Route>
            <Route element={<FeatureFlaggedRoute flag='Onboarding' />}>
              <Route path='/onboarding' element={<OnboardingPage />} />
            </Route>
            <Route element={<InternalRoute />}>
              {/**
               * Internal (QA-Mode) routes
               */}
              <Route
                path='/api-explorer'
                element={
                  <CanopyProvider>
                    <APIExplorer />
                  </CanopyProvider>
                }
              />
              <Route path='/collections' element={<CollectionsPage />} />
              <Route
                path='/collections/:collectionId'
                element={<CollectionDetailsPage />}
              />
            </Route>

            {/** Sandbox page - for local development only */}
            {process.env.NODE_ENV === 'development' && (
              <Route
                path='dev-sandbox'
                element={
                  <CanopyProvider>
                    <DevSandbox />
                  </CanopyProvider>
                }
              />
            )}

            <Route path='*' element={<NotFound />} />
          </Route>
        )}
      </Route>
    </Routes>
  );
};
